/**
 * One-time migration from the legacy "cashee" namespace to "wren".
 * Runs once per browser, gated by a marker key. Safe to call on every load.
 *
 * Migrates:
 *  - localStorage keys prefixed with `cashee_`, `cashee:`, `cashee-` → `wren_`, `wren:`, `wren-`
 *  - IndexedDB database `cashee-local` → `wren-local` (best-effort copy)
 */
const MARKER_KEY = 'wren:legacy-migrated-v2';

/**
 * Global localStorage keys that are NOT user-scoped and would bleed across
 * accounts on shared devices (or after Cashee→Wren rename). Drop them once
 * so each account re-fetches fresh data from the database.
 */
const GLOBAL_KEYS_TO_CLEAR = ['userName'];

function migrateLocalStorage(): number {
  let count = 0;
  try {
    const keys: string[] = [];
    for (let i = 0; i < localStorage.length; i++) {
      const k = localStorage.key(i);
      if (k && /^cashee[_:\-]/.test(k)) keys.push(k);
    }
    for (const oldKey of keys) {
      const newKey = oldKey.replace(/^cashee/, 'wren');
      if (localStorage.getItem(newKey) !== null) {
        // New key already populated — keep it, drop the legacy one.
        localStorage.removeItem(oldKey);
        continue;
      }
      const value = localStorage.getItem(oldKey);
      if (value !== null) {
        localStorage.setItem(newKey, value);
        localStorage.removeItem(oldKey);
        count++;
      }
    }
  } catch (err) {
    console.warn('[legacyMigration] localStorage migration failed', err);
  }
  return count;
}

async function migrateIndexedDB(): Promise<boolean> {
  if (typeof indexedDB === 'undefined') return false;
  try {
    // Check if legacy DB exists. Use databases() when available; otherwise fall back to a probe.
    let legacyExists = false;
    if (typeof (indexedDB as any).databases === 'function') {
      const dbs = await (indexedDB as any).databases();
      legacyExists = dbs.some((d: { name?: string }) => d.name === 'cashee-local');
    } else {
      // Probe by opening — if it's brand new, version will be 1 and objectStoreNames empty.
      const probe = await new Promise<IDBDatabase | null>((resolve) => {
        const req = indexedDB.open('cashee-local');
        req.onsuccess = () => resolve(req.result);
        req.onerror = () => resolve(null);
      });
      if (probe) {
        legacyExists = probe.objectStoreNames.length > 0;
        probe.close();
        if (!legacyExists) {
          indexedDB.deleteDatabase('cashee-local');
        }
      }
    }
    if (!legacyExists) return false;

    // Open legacy DB to read everything
    const legacy = await new Promise<IDBDatabase>((resolve, reject) => {
      const req = indexedDB.open('cashee-local');
      req.onsuccess = () => resolve(req.result);
      req.onerror = () => reject(req.error);
    });

    const storeNames = Array.from(legacy.objectStoreNames);
    const data: Record<string, unknown[]> = {};
    for (const name of storeNames) {
      data[name] = await new Promise<unknown[]>((resolve) => {
        const tx = legacy.transaction(name, 'readonly');
        const req = tx.objectStore(name).getAll();
        req.onsuccess = () => resolve(req.result || []);
        req.onerror = () => resolve([]);
      });
    }
    legacy.close();

    // Stash the data in localStorage so the new Dexie DB can pick it up on first init
    // (avoids importing Dexie here and racing the singleton).
    try {
      localStorage.setItem('wren:legacy-idb-payload', JSON.stringify(data));
    } catch {
      // Quota — skip; user data was already synced to cloud anyway.
    }

    // Drop the legacy DB
    await new Promise<void>((resolve) => {
      const req = indexedDB.deleteDatabase('cashee-local');
      req.onsuccess = () => resolve();
      req.onerror = () => resolve();
      req.onblocked = () => resolve();
    });

    return true;
  } catch (err) {
    console.warn('[legacyMigration] IndexedDB migration failed', err);
    return false;
  }
}

export async function runLegacyMigration(): Promise<void> {
  try {
    if (localStorage.getItem(MARKER_KEY)) return;
    const lsCount = migrateLocalStorage();
    const idbMigrated = await migrateIndexedDB();
    // Drop global non-scoped keys so the next load reads fresh from DB.
    for (const key of GLOBAL_KEYS_TO_CLEAR) {
      try { localStorage.removeItem(key); } catch {}
    }
    localStorage.setItem(MARKER_KEY, new Date().toISOString());
    if (lsCount > 0 || idbMigrated) {
      console.log(`[legacyMigration] migrated ${lsCount} localStorage keys, idb=${idbMigrated}`);
    }
  } catch (err) {
    console.warn('[legacyMigration] failed', err);
  }
}